import React, { Fragment, type FunctionComponent } from 'react'
import styled from '@emotion/styled'
import { fontSize, colors, spacing } from '@retailer-platform/shared-components'
import { Checkbox } from '@retailer-platform/shared-components/src/common/checkbox/Checkbox'
import {
  type SelectProps,
  type SimpleOption,
} from '@retailer-platform/shared-components/src/common/select/utils/select.types'
import { type UseFormReturn } from 'react-hook-form-7'
import { useAccountManagementRoles } from 'domains/account-management/components/role-select/utils/AccountManagementRoleSelect.hooks'
import { type FormData } from 'domains/account-management/components/account-form/utils/AccountForm.types'
import { useAccountManagementRoleSelect } from 'domains/account-management/components/role-select/AccountManagementRoleSelect'

export interface Props<TValue>
  extends Omit<SelectProps<SimpleOption<TValue>>, 'options' | 'isMulti'> {
  form: UseFormReturn<FormData>
  value: FormData['roles']
  onChange: (roles: FormData['roles']) => void
}

export const RoleCheckboxWrapper = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  margin: `${spacing.X12} 0`,
  fontSize: fontSize.X12,
  color: colors.GRAYSCALE.X70,

  '& label:first-of-type': {
    marginRight: spacing.X8,
  },
})

interface OptionType {
  label: string
  description: string
  value: string
  slug: string
  roleRank: 'primary' | 'secondary'
}

export const AccountManagementSecondaryRoleSelect: FunctionComponent<Props<OptionType>> = ({
  value,
  onChange,
  form,
}) => {
  const formData = form.getValues()

  const { loading, primaryOptions, secondaryOptions } = useAccountManagementRoles({
    useDefaultOption: false,
    formData,
  })

  const { secondaryRoleValues, onSecondaryRoleChange } = useAccountManagementRoleSelect({
    value,
    onChange,
    primaryOptions,
  })

  return (
    <Fragment>
      {secondaryOptions &&
        secondaryOptions.map(option => (
          <RoleCheckboxWrapper key={option.slug}>
            <Checkbox
              name={`${option.slug}_role`}
              id={`${option.slug}_role`}
              disabled={loading || option.isDisabled}
              checked={secondaryRoleValues.some(value => value.value === option.value)}
              onChange={event => onSecondaryRoleChange(event, option)}
            />
            <label htmlFor={`${option.slug}_role`}>{option.label}</label>
          </RoleCheckboxWrapper>
        ))}
    </Fragment>
  )
}
